import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ApiError } from '../../../../core/errors/api-error';
import { MessageService } from '../../../../core/services/message/message.service';
import { ClienteCadastro, clienteCadastros } from '../../../../home/pessoa/cliente/cadastro';
import { Cliente } from '../../../../home/pessoa/cliente/cliente';
import { ClienteFilter } from '../../../../home/pessoa/cliente/filter/cliente-filter';
import { ClienteSituacao, clienteSituacoes, isClienteSituacaoInativo } from '../../../../home/pessoa/cliente/situacao/cliente-situacao';
import { IClientePessoa } from '../../../../home/pessoa/cliente/typeahead/cliente-pessoa.interface';
import { ClienteTypeahead } from '../../../../home/pessoa/cliente/typeahead/cliente-typeahead';
import { TypeaheadClienteHttpService } from '../../../../home/pessoa/cliente/typeahead/typeahead-cliente-http.service';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IAutocompleteSettings } from '../autocomplete/interface/settings.interface';

@Component({
  selector: 'sgt-novo-typeahead-cliente-pessoa',
  templateUrl: './novo-typeahead-cliente-pessoa.component.html',
  styleUrls: ['./novo-typeahead-cliente-pessoa.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoTypeaheadClientePessoaComponent),
      multi: true,
    },
  ],
})
export class NovoTypeaheadClientePessoaComponent extends CustomControlValueAccessor<IClientePessoa> implements OnChanges {
  clientesPessoa: IClientePessoa[] = [];
  @Input() selectedClientePessoaId: number | null = null;
  selectedClientePessoa: IClientePessoa | null;
  settings: IAutocompleteSettings = {
    placeholder: 'Digite o nome, código ou CNPJ/CPF do cliente',
    noResultsText: 'Nenhum cliente encontrado',
    disabled: false,
    isLoading: false,
    showBeforeContent: true,
    isEmpty: null
  };

  @Output() clientePessoaSelected = new EventEmitter<IClientePessoa>();

  constructor(
    private el: ElementRef,
    private messageService: MessageService,
    private typeaheadClienteHttpService: TypeaheadClienteHttpService
  ) {
    super();
  }

  ngOnChanges() {
    if (this.selectedClientePessoaId) {
      this.fetchClientePessoaById();
      return;
    }
    this.selectedClientePessoa = null;
    this.clientesPessoa = [];
    this.settings = { ...this.settings, clearFields: true };
  }

  fetchClientesPessoa(searchText: string) {
    this.settings.isLoading = true;
    const filter = new ClienteFilter();
    filter.filterPsq = searchText;
    this.typeaheadClienteHttpService.search(filter, 1000).subscribe(
      (entities: ClienteTypeahead[]) => {
        this.clientesPessoa = this.transformPayloadToAutocompleteItens(entities);
        this.setAutoCompleteSettings();
      },
      (error: ApiError) =>
        this.messageService.error(
          error.message,
          'Erro ao pesquisar os clientes',
          error
        )
    );
  }

  fetchClientePessoaById(): void {
    this.typeaheadClienteHttpService
      .loadByPessoa(this.selectedClientePessoaId)
      .subscribe(
        (res: ClienteTypeahead) => {
          const clientePessoaItem = this.transformPayloadToAutocompleteItens([res])[0];
          this.selectedClientePessoa = clientePessoaItem;
          this.clientesPessoa = [clientePessoaItem];
          this.setAutoCompleteSettings();
          this.settings.isOpenDropdown = false;
        },
        (error: ApiError) =>
          this.messageService.error(
            error.message,
            'Erro ao buscar dados do cliente',
            error
          )
      );
  }

  setAutoCompleteSettings(): void {
    this.settings.isEmpty = this.clientesPessoa.length === 0;
    this.settings.isOpenDropdown = this.clientesPessoa.length > 0;
    this.settings.isLoading = false;

    if (this.settings.isEmpty) {
      this.selectedClientePessoa = null;
    }
  }

  transformPayloadToAutocompleteItens(clientes: ClienteTypeahead[]): IClientePessoa[] {
    return clientes.map((cliente) => ({
      id: cliente.id,
      idPessoa: cliente.idPessoa,
      nome: cliente.pessoa?.nome,
      nomeFantasia: cliente.pessoa?.nomeFantasia,
      cnpjCpf: cliente.pessoa?.cnpjCpf,
      codigoIntegracaoErp: cliente.pessoa?.codigoIntegracaoErp,
      situacao: cliente.situacao,
      nomeCidade: cliente?.nomeCidade,
      uf: cliente?.uf,
      cadastroLabel: this.getClientePessoaCadastro(cliente.cadastro)?.nome,
      cssClassCadastro: this.getClientePessoaCadastro(cliente.cadastro)?.badge,
      situacaoLabel: this.getClientePessoaSituacao(cliente.situacao)?.nome,
      cssClassSituacao: this.getClientePessoaSituacao(cliente.situacao)?.badge
    }));
  }

  getClientePessoaCadastro(numeroCadastro: number): ClienteCadastro | null {
    return clienteCadastros.find((cadastro) => cadastro.id === numeroCadastro) || null;
  }

  getClientePessoaSituacao(numeroSituacao: number): ClienteSituacao | null {
    return clienteSituacoes.find((situacao) => situacao.id === numeroSituacao) || null;
  }

  onSearchClientePessoa(searchText: string): void {
    if (searchText && searchText.length) {
      this.fetchClientesPessoa(searchText);
      return;
    }

    this.clientesPessoa = [];
    this.selectedClientePessoa = null;
    this.settings.isOpenDropdown = false;
    this.clientePessoaSelected.emit(null);
    this.emitChange(null);
  }

  onSelectClientePessoa(clientePessoa: IClientePessoa): void {
    this.selectedClientePessoa = clientePessoa;
    this.settings.isOpenDropdown = false;
    this.clientesPessoa = [];
    this.clientePessoaSelected.emit(clientePessoa);
    this.emitChange(clientePessoa);
  }

  setFocus(): void {
    const input = this.el.nativeElement.querySelector('input');
    if (input) {
      input.focus();
    }
  }

  setEntity(id: number): void {
    this.typeaheadClienteHttpService.load(id).subscribe(
      (res: ClienteTypeahead) => {
        const item = this.transformPayloadToAutocompleteItens([res])[0];
        this.onSelectClientePessoa(item);
      },
      (error: ApiError) =>
        this.messageService.error(error.message, 'Erro ao buscar dados do cliente', error)
    );
  }

  afterInsertCliente(cliente: Cliente): void {
    this.typeaheadClienteHttpService.load(cliente.id).subscribe(
      (res: ClienteTypeahead) => {
        const item = this.transformPayloadToAutocompleteItens([res])[0];
        this.onSelectClientePessoa(item);
      },
      (error: ApiError) =>
        this.messageService.error(error.message, 'Erro ao buscar dados do cliente', error)
    );
  }

  afterEditCliente(cliente: Cliente): void {
    this.typeaheadClienteHttpService.load(cliente.id).subscribe(
      (res: ClienteTypeahead) => {
        const item = this.transformPayloadToAutocompleteItens([res])[0];
        this.selectedClientePessoa = item;
        this.clientesPessoa = [item];
        this.clientePessoaSelected.emit(item);
        this.emitChange(item);
      },
      (error: ApiError) =>
        this.messageService.error(error.message, 'Erro ao buscar dados do cliente', error)
    );
  }

  isInativo(situacao: number): boolean {
    return isClienteSituacaoInativo(situacao);
  }

  protected updateValue(newValue: IClientePessoa | null): void {
    this.selectedClientePessoa = newValue;
    if (!newValue) {
      this.settings = { ...this.settings, clearFields: true };
    }
  }
}
